import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BellRing, LogIn, UserX } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiMessage } from '../lib/helpers';
import type { WaitingListTicket } from './kids-area';

interface KidsWaitingListProps {
  resourceId: string;
  tickets: WaitingListTicket[];
  loading: boolean;
  /** Places free right now — promoting into a full area is refused server-side. */
  free: number;
  onChanged: () => void;
}

/** §11 — the queue for a full kids area: who is next, seat them, or let them go. */
export function KidsWaitingList({
  resourceId,
  tickets,
  loading,
  free,
  onChanged,
}: KidsWaitingListProps) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [minutes, setMinutes] = useState('60');
  const [busyTicket, setBusyTicket] = useState<string | null>(null);

  // Seating and joining are the same act to the API, so they share its key.
  const canManage = can('resource_sessions.start');

  const run = async (ticketId: string, action: () => Promise<void>, successKey: string) => {
    setBusyTicket(ticketId);
    try {
      await action();
      toast.success(t(successKey));
      onChanged();
    } catch (error) {
      toast.error(apiMessage(error, t('spaces.resources.actionFailed')));
    } finally {
      setBusyTicket(null);
    }
  };

  const promote = (ticket: WaitingListTicket) =>
    run(
      ticket.ticketId,
      async () => {
        await api.post(`/kids-area/waiting-list/${ticket.ticketId}/promote`, {
          // `resourceId` is required by the shared check-in DTO even though the
          // ticket already names the area — omitting it is a 400.
          resourceId,
          ...(Number(minutes) > 0 ? { plannedMinutes: Math.round(Number(minutes)) } : {}),
        });
      },
      'spaces.kids.promoted',
    );

  const cancel = (ticket: WaitingListTicket) =>
    run(
      ticket.ticketId,
      async () => {
        await api.post(`/kids-area/waiting-list/${ticket.ticketId}/cancel`, { reason: 'no_show' });
      },
      'spaces.kids.queueCancelled',
    );

  if (loading && tickets.length === 0) {
    return <p className="text-xs text-ink-muted">{t('common.loading')}</p>;
  }
  if (tickets.length === 0) {
    return <p className="text-xs text-ink-muted">{t('spaces.kids.queueEmpty')}</p>;
  }

  return (
    <div className="space-y-2">
      {canManage && (
        <label className="flex items-center gap-2 text-xs text-ink-muted">
          {t('spaces.kids.seatMinutes')}
          <Input
            type="number"
            min={1}
            value={minutes}
            onChange={(event) => setMinutes(event.currentTarget.value)}
            className="h-8 w-24"
          />
        </label>
      )}

      <ul className="divide-y rounded-lg border">
        {tickets.map((ticket) => {
          const busy = busyTicket === ticket.ticketId;
          return (
            <li key={ticket.ticketId} className="space-y-2 p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="font-medium">
                  <span className="me-2 tabular-nums text-ink-muted">
                    #{ticket.position ?? '—'}
                  </span>
                  {ticket.childName ?? t('spaces.kids.walkIn')}
                  {ticket.tagCode && (
                    <span className="ms-2 text-xs text-ink-muted" dir="ltr">
                      {ticket.tagCode}
                    </span>
                  )}
                </span>
                {ticket.status === 'CALLED' && (
                  <Badge tone="warning">
                    <BellRing className="me-1 h-3 w-3" />
                    {t('spaces.kids.called')}
                  </Badge>
                )}
              </div>

              <p className="text-xs text-ink-muted">
                {ticket.guardianName
                  ? t('spaces.kids.withGuardian', { name: ticket.guardianName })
                  : t('spaces.kids.noGuardian')}
                {ticket.partySize > 1 &&
                  ` · ${t('spaces.kids.headCount', { count: ticket.partySize })}`}
                {ticket.note && ` · ${ticket.note}`}
              </p>

              {canManage && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || free <= 0 || !ticket.childId}
                    // A walk-in note has nobody to bill or link to a guardian;
                    // the API refuses it, so the button says so first.
                    title={
                      !ticket.childId
                        ? t('spaces.kids.promoteNeedsGuest')
                        : free <= 0
                          ? t('spaces.kids.promoteNeedsSpace')
                          : undefined
                    }
                    onClick={() => void promote(ticket)}
                  >
                    <LogIn className="h-4 w-4" />
                    {t('spaces.kids.promote')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void cancel(ticket)}
                  >
                    <UserX className="h-4 w-4" />
                    {t('spaces.kids.cancelTicket')}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
